import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, RefreshCw, AlertCircle, FileText } from 'lucide-react';

const AdminUserConsultations = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState({ user: null, stats: null, consultations: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const [detail, list] = await Promise.all([
          api.admin.getUserDetail(userId),
          api.admin.getUserConsultations(userId, 100, 0),
        ]);
        if (mounted) setData({ user: detail.user, stats: detail.stats, consultations: list.consultations || [] });
      } catch (err) {
        if (mounted) setError(err.response?.data?.detail || 'Erro ao carregar dados do usuário.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [userId]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
          <span className="ml-2">Carregando...</span>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-64">
          <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
          <p className="text-red-500 text-lg">{error}</p>
          <Button onClick={() => navigate('/admin')} className="mt-4">Voltar</Button>
        </div>
      </DashboardLayout>
    );
  }

  const { user, stats, consultations } = data;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => navigate('/admin')} data-testid="back-to-admin">
            <ArrowLeft className="w-4 h-4 mr-2" /> Voltar ao painel
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{user?.full_name || user?.email}</CardTitle>
            <CardDescription>{user?.email}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
              <div><span className="text-gray-500">Plano:</span> <Badge>{user?.plan_type || 'starter'}</Badge></div>
              <div><span className="text-gray-500">Status:</span> {user?.subscription_status || '—'}</div>
              <div><span className="text-gray-500">Pacientes:</span> {stats?.patient_count || 0}</div>
              <div><span className="text-gray-500">Consultas:</span> {stats?.consultation_count || 0}</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Consultas ({consultations.length})</CardTitle>
            <CardDescription>Lista cronológica das consultas deste usuário</CardDescription>
          </CardHeader>
          <CardContent>
            {consultations.length === 0 ? (
              <div className="text-center text-gray-500 py-12">
                <FileText className="w-10 h-10 mx-auto mb-2 text-gray-400" />
                Nenhuma consulta registrada.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="user-consultations-table">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-2">Data</th>
                      <th className="text-left py-3 px-2">Tipo</th>
                      <th className="text-left py-3 px-2">Especialidade</th>
                      <th className="text-left py-3 px-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {consultations.map((c) => (
                      <tr key={c.id} className="border-b">
                        <td className="py-2 px-2">
                          {c.created_at ? new Date(c.created_at).toLocaleString('pt-BR') : '—'}
                        </td>
                        <td className="py-2 px-2">{c.consultation_type || '—'}</td>
                        <td className="py-2 px-2">{c.template_type || '—'}</td>
                        <td className="py-2 px-2">
                          <Badge variant="outline">{c.status || 'draft'}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AdminUserConsultations;
